import { useMemo } from 'react';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { useNavigate } from 'react-router-dom';
import DashboardKPITile from '@/components/DashboardKPITile';
import ActivityFeedItem from '@/components/ActivityFeedItem';
import { customerName } from '@/utils/format';
import { networkStudioCounts, networkTotals } from '@/domain/networkView';
import type { AlertTriangle } from 'lucide-react';
import { RotateCcw, ChevronRight, PhoneMissed } from 'lucide-react';

export default function NetworkView() {
  const studios = useBrainStore((s) => s.studios);
  const stats = useBrainStore((s) => s.todayStats);
  const customers = useBrainStore((s) => s.customers);
  const deposits = useBrainStore((s) => s.deposits);
  const bookings = useBrainStore((s) => s.bookings);
  const aiConversations = useBrainStore((s) => s.aiConversations);
  const campaigns = useBrainStore((s) => s.reactivationCampaigns);
  const activity = useBrainStore((s) => s.activityFeed);
  const clockIso = useSimulationStore((s) => s.clockIso);
  const navigate = useNavigate();

  // Store-derived operational counts - no hardcoded decorations. Slices in the
  // deps so the cards move as the store changes.
  const { countsById, totals } = useMemo(() => {
    const rows = networkStudioCounts(clockIso);
    return {
      countsById: new Map(rows.map((r) => [r.studioId, r])),
      totals: networkTotals(rows),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clockIso, studios, customers, deposits, bookings]);

  const escalations = useMemo(
    () =>
      Object.values(customers)
        .filter(
          (c) =>
            c.leadScore >= 80 ||
            c.leadStage === 'deposit-pending' ||
            c.engagementStatus === 'warm',
        )
        .slice(0, 6),
    [customers],
  );

  const aiLive = Object.values(aiConversations);
  const campaign = Object.values(campaigns)[0];

  return (
    <section className="px-10 py-8" data-testid="network-view">
      <header className="flex items-end justify-between max-w-6xl">
        <div>
          <p className="heading-eyebrow">Network · Operations</p>
          <h1 className="heading-display text-4xl text-cream mt-3">Network View</h1>
          <p className="body-serif-soft text-base mt-4 max-w-2xl">
            Eleven studios. Today&apos;s deposits, missed calls, AI hand-offs and queue depth — every
            count read live from the store.
          </p>
        </div>
        <div className="flex gap-3">
          <button type="button" className="btn-ghost text-xs" onClick={() => navigate('/reactivation')}>
            <RotateCcw size={12} /> Reactivation Engine
          </button>
        </div>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 max-w-6xl">
        <DashboardKPITile
          label="Network deposits today"
          value={stats.deposits.toString()}
          accent
          info={<>Deposits paid across every studio so far today.</>}
        />
        <DashboardKPITile
          label="Missed calls"
          value={totals.missedCalls.toString()}
          caption="Across eleven studios"
          info={
            <>
              Unreturned inbound calls in the studios&apos; queues - a voicemail in the last 72 hours
              with no call back yet. Summed from the live queue.
            </>
          }
        />
        <DashboardKPITile
          label="Cross-studio routes"
          value={stats.crossStudioRoutes.toString()}
          info={<>Calls answered for one studio and routed to another today.</>}
        />
        <DashboardKPITile
          label="Queue depth"
          value={totals.queueDepth.toString()}
          caption="Open across the network"
          info={
            <>
              Total items waiting in every studio&apos;s working queue right now (pending deposits,
              callbacks, missed calls and follow-ups). Straight from the queue engine.
            </>
          }
        />
      </div>

      <div className="mt-10 grid grid-cols-12 gap-6 max-w-6xl">
        <section className="col-span-12 lg:col-span-7">
          <div className="flex items-center justify-between mb-3">
            <h2 className="heading-serif text-lg text-cream">Studios</h2>
            <span className="font-inter text-[11px] text-cream-soft uppercase tracking-widest">
              {Object.values(studios).length} live
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Object.values(studios).map((s) => {
              const c = countsById.get(s.id) ?? {
                inquiries: 0,
                depositsReady: 0,
                missedCalls: 0,
                queueDepth: 0,
              };
              const accent = c.missedCalls > 0;
              return (
                <button
                  key={s.id}
                  data-testid={`studio-card-${s.id}`}
                  onClick={() => navigate(`/studio/${s.id}`)}
                  className={
                    accent
                      ? 'text-left panel p-4 border-gold-bright/50 hover:shadow-gold-glow transition'
                      : 'text-left panel p-4 hover:border-gold-bright/40 transition'
                  }
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-inter text-[9px] text-gold-bright uppercase tracking-widest">
                        {s.brand}
                      </p>
                      <p className="font-inter text-base text-cream mt-0.5">{s.name}</p>
                      <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-1">
                        {s.state} · {s.brandedNumber} · {s.artistCount} artists
                      </p>
                    </div>
                    {s.isBlackBook ? <span className="pill-cream text-[10px]">Black Book</span> : null}
                  </div>
                  <div className="grid grid-cols-4 gap-2 mt-3 text-[11px]">
                    <Stat label="Inquiries" value={c.inquiries} />
                    <Stat label="Deposits" value={c.depositsReady} accent />
                    <Stat
                      label="Missed"
                      value={c.missedCalls}
                      Icon={c.missedCalls > 0 ? PhoneMissed : undefined}
                      blood={c.missedCalls > 0}
                    />
                    <Stat label="Queue" value={c.queueDepth} />
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        <aside className="col-span-12 lg:col-span-5 space-y-6">
          <div>
            <h2 className="heading-serif text-lg text-cream mb-3">Escalations</h2>
            <ol className="space-y-2">
              {escalations.length === 0 ? (
                <li className="text-sm text-cream-soft italic">All clear.</li>
              ) : null}
              {escalations.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => navigate(`/customer/${c.id}`)}
                    className="w-full text-left panel-dark p-3 flex items-center justify-between hover:border-gold-bright/40 transition"
                    data-testid={`escalation-${c.id}`}
                  >
                    <div>
                      <p className="font-inter text-sm text-cream">{customerName(c)}</p>
                      <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-1">
                        {c.leadStage} · score {c.leadScore} · {c.primaryStudioId.replace('-', ' ')}
                      </p>
                    </div>
                    <ChevronRight size={14} className="text-gold-bright" />
                  </button>
                </li>
              ))}
            </ol>
          </div>

          <div>
            <h2 className="heading-serif text-lg text-cream mb-3">AI status</h2>
            <div className="panel p-4 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-cream-soft">Live AI conversations</span>
                <span className="font-mono text-gold-bright">{aiLive.length}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-cream-soft">AI hand-offs pending</span>
                <span className="font-mono text-gold-bright">
                  {aiLive.filter((c) => c.state === 'hand-off-requested').length}
                </span>
              </div>
              {campaign ? (
                <>
                  <div className="divider-gold" />
                  <p className="label-small-caps">Reactivation</p>
                  <p className="text-sm text-cream">{campaign.segmentName}</p>
                  <div className="flex justify-between font-mono text-xs">
                    <span className="text-cream-soft">Calls overnight</span>
                    <span className="text-gold-bright">{campaign.callsCompleted}</span>
                  </div>
                  <div className="flex justify-between font-mono text-xs">
                    <span className="text-cream-soft">Warm hand-offs ready</span>
                    <span className="text-mint">{campaign.warmHandoffs}</span>
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </aside>
      </div>

      <section className="mt-10 max-w-6xl">
        <h2 className="heading-serif text-lg text-cream mb-3">Recent activity</h2>
        <div className="space-y-2">
          {activity.slice(0, 6).map((entry) => (
            <ActivityFeedItem key={entry.id} entry={entry} />
          ))}
        </div>
      </section>
    </section>
  );
}

function Stat({
  label,
  value,
  accent,
  blood,
  Icon,
}: {
  label: string;
  value: number;
  accent?: boolean;
  blood?: boolean;
  Icon?: typeof AlertTriangle;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-cream-soft uppercase tracking-widest text-[9px]">{label}</span>
      <span
        className={
          'flex items-center gap-1 font-mono ' +
          (blood ? 'text-blood' : accent ? 'text-gold-bright' : 'text-cream')
        }
      >
        {Icon ? <Icon size={10} /> : null}
        {value}
      </span>
    </div>
  );
}
